// Dependencies
import React, { useState } from "react";
import { useRouter } from "next/router";

// Material UI Components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Popover from "@mui/material/Popover";
import Grid from "@mui/material/Grid";

// Components
import FilterModal from "features/inventory/components/FilterModal/FilterModal.component";
import MakeModelButton from "features/inventory/components/MakeModelButton/MakeModelButton.component";
import FilterOption from "features/inventory/components/FilterOptions/FilterOption.component";
import ClearAllFiltersButton from "features/inventory/components/ClearAllFiltersButton/ClearAllFiltersButton.component";
import FilterDrawer from "features/inventory/components/FilterDrawer/FilterDrawer.component";

// Helpers
import useInventoryFilters from "hooks/inventory/useInventoryFilters.hook";
import {
  getPathnameItem,
  removeQueryOrSlugItem,
  replaceSlugAndQuery,
} from "helpers/inventory-filters";
import usePageWidth from "hooks/usePageWidth.hook";
import SearchButton from "features/inventory/components/SearchButton/SearchButton.component";
import FilterModalButton from "features/inventory/components/FilterModalButton/FilterModalButton.component";

// Styles
import { useStyles } from "./FilterMenu.styles";

const FilterMenu = ({
  attributes,
  listingsCount,
  clearInventoryCards,
  locationFormProps,
}) => {
  const classes = useStyles();
  const router = useRouter();
  const { isDesktopWidth } = usePageWidth();
  const { filters: inventoryFilters } = useInventoryFilters();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (e) => {
    setOpen(true);
    if (isDesktopWidth) {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleClear = () => {
    clearInventoryCards();
    const { newQuery, newSlug } = removeQueryOrSlugItem(router.query, {
      key: "make",
      value: inventoryFilters.make[0],
    });
    replaceSlugAndQuery(router, newSlug, newQuery);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderMenuDrawer = () => (
    <FilterDrawer
      attributes={attributes}
      clearInventoryCards={clearInventoryCards}
      listingsCount={listingsCount}
      open={open}
      handleClose={handleClose}
      title="Edit"
      contents={
        <>
          <Grid container className={classes.select}>
            <FilterOption
              clearInventoryCards={clearInventoryCards}
              attributes={attributes}
              keyName="make"
              defaultValueLabel="Any make"
              indexOfPath={2}
            />
          </Grid>
          <Grid container className={classes.select}>
            <FilterOption
              clearInventoryCards={clearInventoryCards}
              attributes={attributes}
              keyName="model"
              defaultValueLabel="Any model"
              indexOfPath={3}
              isDisabled={!getPathnameItem(router.query.slug, "make")}
            />
          </Grid>
        </>
      }
      actionMenuContents={
        <>
          <ClearAllFiltersButton overrideOnClick={handleClear} />
          <div className="flexGrow" />
          <SearchButton
            listingsCount={listingsCount}
            onClick={handleClose}
            data-cy="save-make-model-modal"
          />
        </>
      }
    />
  );

  const renderMenuPopover = () => (
    <Popover
      classes={{ paper: classes.makeModelDropdown }}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <div className={classes.menuContentsContainer}>
        <span className={classes.makeModelFormContainer}>
          <FilterOption
            clearInventoryCards={clearInventoryCards}
            attributes={attributes}
            keyName="make"
            defaultValueLabel="Any make"
            isDisabled={false}
          />
        </span>
        <span className={classes.makeModelFormContainer}>
          <FilterOption
            clearInventoryCards={clearInventoryCards}
            attributes={attributes}
            keyName="model"
            defaultValueLabel="Any model"
            isDisabled={!getPathnameItem(router.query.slug, "make")} // disabled if make doesnt exist
          />
        </span>
        <div className="flexGrow" />
        <div className={classes.makeModelBottomContents}>
          <ClearAllFiltersButton overrideOnClick={handleClear} />
          <div className="flexGrow" />
          <SearchButton
            listingsCount={listingsCount}
            onClick={handleClose}
            data-cy="save-make-model-modal"
          />
        </div>
      </div>
    </Popover>
  );

  return (
    <AppBar className={classes.appBar} color="transparent">
      <Toolbar disableGutters className={classes.toolBar}>
        <MakeModelButton
          clearInventoryCards={clearInventoryCards}
          onClick={handleClick}
        />
        {isDesktopWidth ? renderMenuPopover() : renderMenuDrawer()}
        {isDesktopWidth && <FilterModalButton buttonLabel="Filter" />}
        <FilterModal
          listingsCount={listingsCount}
          clearInventoryCards={clearInventoryCards}
          attributes={attributes}
          locationFormProps={locationFormProps}
        />
      </Toolbar>
    </AppBar>
  );
};

FilterMenu.propTypes = {};

FilterMenu.defaultProps = {};

export default FilterMenu;
